import { useEffect, useState } from "react";
import type { MediaFormat } from "@hybrid-iptv/types";

/**
 * What the provider measured in an episode's file (Episode.media), put to use
 * (owner, 2026-10-09): a quality label on the episode, and inside the app a
 * warning on an episode this device's decoders cannot show. Films carry no
 * measurement, so they get neither.
 */

/** "1080p · HDR · 5.1": the picture's size class, HDR, and surround sound. Null when there is nothing to say. */
export function qualityLabel(media: MediaFormat | undefined): string | null {
  if (!media) return null;
  const parts: string[] = [];
  const width = media.width ?? 0;
  const height = media.height ?? 0;
  if (width > 0 || height > 0) {
    // By width OR height, so wide films (1920×800) and 4:3 shows (1440 tall) land right.
    parts.push(width >= 3200 || height >= 2000 ? "4K" : width >= 1800 || height >= 1000 ? "1080p" : width >= 1200 || height >= 700 ? "720p" : "SD");
  }
  if (media.hdr) parts.push(media.hdr === "hlg" ? "HLG" : "HDR");
  if (media.audioChannels === 6) parts.push("5.1");
  else if (media.audioChannels !== undefined && media.audioChannels >= 8) parts.push("7.1");
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** One video type's limits on this device (NativePlayerPlugin.decoders). */
export interface DecoderLimits {
  maxWidth: number;
  maxHeight: number;
  tenBit: boolean;
}

export type DeviceDecoders = Record<string, DecoderLimits>;

/** ffprobe's codec names → Android's video types. Anything else is not judged. */
const MIME: Record<string, string> = {
  h264: "video/avc",
  hevc: "video/hevc",
  h265: "video/hevc",
  mpeg4: "video/mp4v-es",
  mpeg2video: "video/mpeg2",
  vp8: "video/x-vnd.on2.vp8",
  vp9: "video/x-vnd.on2.vp9",
  av1: "video/av01",
};

/**
 * Whether this device can show the picture: false only when it clearly cannot
 * (no decoder for the type, a picture larger than any decoder takes, or 10-bit
 * where none plays 10-bit), null when there is no way to tell. A warning, never
 * a block: a decoder's own list can undersell it.
 */
export function playsHere(media: MediaFormat | undefined, decoders: DeviceDecoders | null): boolean | null {
  if (!media?.videoCodec || !decoders || Object.keys(decoders).length === 0) return null;
  const mime = MIME[media.videoCodec];
  if (!mime) return null;
  const limits = decoders[mime];
  if (!limits) return false;
  const width = media.width ?? 0;
  const height = media.height ?? 0;
  // Decoders take a picture either way up.
  const fits = (width <= limits.maxWidth && height <= limits.maxHeight) || (width <= limits.maxHeight && height <= limits.maxWidth);
  if (!fits) return false;
  if ((media.bitDepth ?? 8) > 8 && !limits.tenBit) return false;
  return true;
}

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
}

let decodersOnce: Promise<DeviceDecoders | null> | null = null;

/** This device's decoders, asked once; null on the website (its server converts what browsers cannot play). */
export function deviceDecoders(): Promise<DeviceDecoders | null> {
  if (!decodersOnce) {
    const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
    decodersOnce =
      cap?.isNativePlatform?.() === true && typeof cap.nativePromise === "function"
        ? cap.nativePromise("NativePlayer", "decoders", {}).then(
            (answer) => (answer && typeof answer === "object" ? (answer as DeviceDecoders) : null),
            () => null,
          )
        : Promise.resolve(null);
  }
  return decodersOnce;
}

/** True when this device clearly cannot show the episode's picture. */
export function useMayNotPlay(media: MediaFormat | undefined): boolean {
  const [mayNotPlay, setMayNotPlay] = useState(false);
  useEffect(() => {
    if (!media?.videoCodec) return;
    let live = true;
    void deviceDecoders().then((decoders) => {
      if (live) setMayNotPlay(playsHere(media, decoders) === false);
    });
    return () => {
      live = false;
    };
  }, [media]);
  return mayNotPlay;
}

/** The picture format in words, for the warning: "HEVC 10-bit 4K". */
export function pictureName(media: MediaFormat): string {
  const codec = media.videoCodec === "hevc" || media.videoCodec === "h265" ? "HEVC" : media.videoCodec === "h264" ? "H.264" : media.videoCodec === "mpeg4" ? "MPEG-4" : (media.videoCodec ?? "").toUpperCase();
  const size = qualityLabel({ ...(media.width ? { width: media.width } : {}), ...(media.height ? { height: media.height } : {}) });
  return [codec, (media.bitDepth ?? 8) > 8 ? `${media.bitDepth}-bit` : null, size].filter(Boolean).join(" ");
}
